// Speciality ↔ Compucare category ID linking (the "workaround" relationship).
//
// The speciality page mapping (specialityPageMapping.json) and the blogs data
// carry speciality NAMES only, while the Compucare-derived datasets
// (generated/doctors.json, ccsdLinkedData.json) carry `categoryId`s.
// This module derives the missing relationship:
//
//   speciality name ──(name matching)──▶ categoryId(s)
//
// Built lazily from:
//   - generated/doctors.json     (doctor speciality names + categoryId)
//   - ccsdLinkedData.json        (categoryName / specialityName + categoryId)

import generatedDoctors from '@/generated/doctors.json';

import ccsdLinkedData from './ccsdLinkedData.json';
import { normaliseSpecialityName } from './specialityPageMapping';

interface RawDoctorIndexEntry {
  slug?: string;
  primarySpeciality?: string | null;
  specialities?: string[] | null;
  categoryId?: string | null;
}

interface RawCcsdLinkedEntry {
  categoryId?: string | null;
  categoryName?: string | null;
  specialityName?: string | null;
}

/**
 * Containment-aware speciality name matcher.
 * "Trauma & Orthopaedics" matches "Trauma & Orthopaedics (Lower Limb)".
 * Containment is only applied when the shorter name has >= 5 alphanumeric
 * characters, to avoid false positives like "ENT" ⊂ "Dental".
 */
export const specialityNamesMatch = (a: string, b: string): boolean => {
  const na = normaliseSpecialityName(a);
  const nb = normaliseSpecialityName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const shorter = na.length <= nb.length ? na : nb;
  if (shorter.length < 5) return false;
  return na.includes(nb) || nb.includes(na);
};

// slug → categoryId (from the generated Compucare doctor index).
const doctorCategoryBySlug = new Map<string, string>();
for (const raw of generatedDoctors as RawDoctorIndexEntry[]) {
  if (raw.slug && raw.categoryId) {
    doctorCategoryBySlug.set(raw.slug, raw.categoryId);
  }
}

export const getCategoryIdForDoctorSlug = (slug: string): string | null =>
  doctorCategoryBySlug.get(slug) ?? null;

// Lazy-built name → categoryIds map.
let nameToCategoryIds: Map<string, Set<string>> | null = null;

const buildNameIndex = (): Map<string, Set<string>> => {
  if (nameToCategoryIds) return nameToCategoryIds;
  nameToCategoryIds = new Map();
  const add = (name: string | null | undefined, categoryId: string) => {
    const key = normaliseSpecialityName(name ?? '');
    if (!key || !categoryId) return;
    const bucket = nameToCategoryIds!.get(key) ?? new Set<string>();
    bucket.add(categoryId);
    nameToCategoryIds!.set(key, bucket);
  };

  for (const raw of generatedDoctors as RawDoctorIndexEntry[]) {
    if (!raw.categoryId) continue;
    add(raw.primarySpeciality, raw.categoryId);
    for (const name of raw.specialities ?? []) {
      add(name, raw.categoryId);
    }
  }

  for (const raw of ccsdLinkedData as RawCcsdLinkedEntry[]) {
    if (!raw.categoryId) continue;
    add(raw.categoryName, raw.categoryId);
    add(raw.specialityName, raw.categoryId);
  }

  return nameToCategoryIds;
};

/**
 * Resolves the Compucare category IDs associated with a speciality name
 * (exact-normalised match plus containment matching).
 */
export const getCategoryIdsForSpecialityName = (name: string): string[] => {
  const index = buildNameIndex();
  const target = normaliseSpecialityName(name);
  if (!target) return [];

  const exact = index.get(target);
  const ids = new Set<string>(exact ? [...exact] : []);

  for (const [key, bucket] of index) {
    if (key === target) continue;
    if (specialityNamesMatch(key, target)) {
      for (const id of bucket) ids.add(id);
    }
  }
  return [...ids];
};

/** Category IDs for a batch of speciality names. */
export const getCategoryIdsForSpecialityNames = (
  names: Iterable<string>,
): string[] => {
  const ids = new Set<string>();
  for (const name of names) {
    for (const id of getCategoryIdsForSpecialityName(name)) {
      ids.add(id);
    }
  }
  return [...ids];
};
